/**
 * O que a tela faz com a escolha de um veículo do SGR (spec 032, §4–§7) —
 * PURO (sem React e sem alias `@/`), provado na API
 * (apps/api/src/shared/sgr-view.test.ts), como a ficha da 031.
 *
 * Nenhum texto daqui traz palavra de vertical (guarda de vocabulário, spec
 * 017): o recurso, o identificador, o contato e o cliente vêm dos termos do
 * tenant, e o nome da origem ("SGR") vem da API (`sourceName`).
 */
import {
  sgrTicketTitle,
  TICKET_SHEET_SLOTS,
  type SgrPickResponse,
  type SgrRefreshResponse,
  type SgrRoutineCounts,
  type SheetSgrRefreshResponse,
  type Terms,
  type TicketSheetSlot,
} from '@erp/shared';

type PickOk = Extract<SgrPickResponse, { status: 'ok' }>;
type ClientRef = { id: number; name: string };

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ── o "Novo ticket" (§4) ────────────────────────────────────────────────────

/** o que fazer com o campo Cliente depois da escolha */
export type PickClientAction =
  /** o ponto de venda está ligado e o campo estava vazio: preenche */
  | { kind: 'set'; client: ClientRef }
  /** ligado, e a pessoa já tinha escolhido OUTRO: pergunta antes de trocar */
  | { kind: 'ask'; client: ClientRef }
  /** ligado ao mesmo cliente que já estava: nada */
  | { kind: 'keep' }
  /** o ponto não está ligado: a pessoa escolhe o cliente do ticket (nada é adivinhado) */
  | { kind: 'needs_client'; salePointName: string | null };

export interface NewTicketPickPlan {
  /** a ficha do formulário: o que o SGR tem entra; onde o SGR não tem nada, fica o que já estava digitado */
  sheet: Record<TicketSheetSlot, string>;
  /** o título sugerido `PLACA-CIDADE-NOME`, só quando o título está vazio */
  title: string;
  client: PickClientAction;
  equipment: PickOk['equipment'];
  receipt: string;
  inactive: boolean;
  vehicleStatus: string | null;
}

export function newTicketPickPlan(
  pick: PickOk,
  current: { sheet: Record<TicketSheetSlot, string>; title: string; client: ClientRef | null }
): NewTicketPickPlan {
  const sheet = { ...current.sheet };
  for (const slot of TICKET_SHEET_SLOTS) {
    const v = pick.sheet[slot];
    if (v !== null && v !== '') sheet[slot] = v;
  }
  const linked = pick.salePoint?.client ?? null;
  const client: PickClientAction = !linked
    ? { kind: 'needs_client', salePointName: pick.salePoint?.name ?? null }
    : current.client === null
      ? { kind: 'set', client: linked }
      : current.client.id === linked.id
        ? { kind: 'keep' }
        : { kind: 'ask', client: linked };
  return {
    sheet,
    title: current.title.trim() !== '' ? current.title : (sgrTicketTitle(pick.sheet) ?? current.title),
    client,
    equipment: pick.equipment,
    receipt: pick.receipt,
    inactive: pick.inactive,
    vehicleStatus: pick.vehicleStatus,
  };
}

/**
 * O recurso escolhido serve ao cliente do ticket? O veículo sem associação
 * (ponto sem cliente) serve a qualquer um; o de um cliente, só a ele — senão a
 * API recusaria (`equipment_not_of_client`).
 */
export function pickedEquipmentFits(equipment: { clientId: number | null }, clientId: number | null): boolean {
  return equipment.clientId === null || clientId === null || equipment.clientId === clientId;
}

/** "Este veículo é de X no SGR; o atendimento é de Y." — sem palavra de vertical escrita aqui */
export function equipmentOfOtherClientText(
  equipmentClientName: string | null,
  ticketClientName: string,
  terms: Pick<Terms, 'resource' | 'ticket'>,
  sourceName: string
): string {
  return `Este ${terms.resource} é de ${equipmentClientName ?? 'outro cliente'} no ${sourceName}; o ${terms.ticket} é de ${ticketClientName}.`;
}

// ── o campo Recurso de um ticket existente (§4) ─────────────────────────────

/**
 * Slot VAZIO na ficha efetiva é preenchido; o que tem valor fica ("No SGR: X",
 * com "usar"). Mora no @erp/shared desde a spec 035: a rotina noturna do
 * Recurso usa a MESMA régua no servidor.
 */
export { existingTicketPickPlan, type ExistingTicketPickPlan } from '@erp/shared';

// ── frescor (§6) ────────────────────────────────────────────────────────────

/** "atualizado agora", "há 5 min", "há 3 h", "há 2 dias" */
export function updatedAgoText(at: string | Date | null, now: Date = new Date()): string {
  if (!at) return 'nunca atualizado';
  const t = new Date(at).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.max(0, Math.floor((now.getTime() - t) / 60_000));
  if (min < 1) return 'atualizado agora';
  if (min < 60) return `atualizado há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `atualizado há ${h} h`;
  const d = Math.floor(h / 24);
  return `atualizado há ${d} ${d === 1 ? 'dia' : 'dias'}`;
}

/** o que dizer depois do "Atualizar do SGR" da página */
export function pageRefreshText(res: SgrRefreshResponse, sourceName: string): string {
  if (res.status === 'ok') return res.refreshed ? `Atualizado do ${sourceName}.` : 'Já estava atualizado (menos de 1 minuto).';
  if (res.status === 'not_found') return `Não encontrado no ${sourceName} — nada foi apagado aqui.`;
  return unavailableText(sourceName, res.reason);
}

/** o que dizer depois do "Atualizar do SGR" do cartão da ficha */
export function sheetRefreshText(
  res: SheetSgrRefreshResponse,
  sourceName: string,
  label: (slot: TicketSheetSlot) => string
): string {
  if (res.status === 'not_found') return `Não encontrado no ${sourceName} — a ficha ficou como estava.`;
  if (res.status === 'unavailable') return unavailableText(sourceName, res.reason);
  const parts: string[] = [];
  if (res.updated.length === 0 && res.kept.length === 0) return `A ficha já estava igual ao ${sourceName}.`;
  if (res.updated.length > 0) {
    parts.push(`${res.updated.length === 1 ? '1 campo atualizado' : `${res.updated.length} campos atualizados`}: ${res.updated.map((s) => label(s as TicketSheetSlot)).join(', ')}`);
  }
  if (res.kept.length > 0) {
    // não só o editado: no importado, o slot da descrição diferente do SGR também
    // fica. "Veja No SGR" só quando o cartão tem um "No SGR: X" para mostrar
    const n = res.kept.length;
    const see = res.kept.some((k) => k.sgrValue !== null) ? ` (veja "No ${sourceName}")` : '';
    parts.push(
      `${n === 1 ? '1 campo diferente' : `${n} campos diferentes`} do ${sourceName} ${n === 1 ? 'ficou como estava' : 'ficaram como estavam'}${see}`
    );
  }
  return `${parts.join('; ')}.`;
}

/**
 * A escolha não leu o veículo (§4, passo 1): nada foi gravado, e a tela segue
 * com o resultado da busca. `manual`: no "Novo ticket", a saída é a ficha à mão.
 */
export function pickFailedText(
  res: Exclude<SgrPickResponse, PickOk>,
  sourceName: string,
  terms: Pick<Terms, 'resource'>,
  manual: boolean
): string {
  const tail = manual ? ' — preencha a ficha à mão.' : '.';
  if (res.status === 'not_found') return `Não encontrado no ${sourceName}${tail}`;
  if (res.reason === 'rate_limited') return unavailableText(sourceName, res.reason);
  return `Não foi possível ler o ${terms.resource} no ${sourceName}${tail}`;
}

/** "SGR indisponível — preencha a ficha à mão" (o nome vem da API) */
export function unavailableText(sourceName: string, reason?: string): string {
  if (reason === 'rate_limited') return 'Muitas buscas seguidas — espere um instante.';
  return `${sourceName} indisponível — preencha a ficha à mão.`;
}

// ── "Ver veículo" e "Ver associado" do cartão (§7) ──────────────────────────

export type VehicleLinkTarget =
  /** o recurso do ERP já está ligado ao ticket */
  | { kind: 'equipment'; equipmentId: number }
  /** o ticket importado traz o `ID_SGR:` (ou a foto da escolha): abre pelo id do SGR */
  | { kind: 'source_id'; sgrId: string }
  /** sem id: busca pela placa e escolhe só se o resultado for exatamente 1 */
  | { kind: 'plate'; plate: string };

export function vehicleLinkTarget(input: {
  equipment: { id: number } | null;
  /** o recurso ligado é um veículo do SGR (o ticket traz o associado dele) */
  equipmentIsSgr: boolean;
  /** o "Código do cadastro" da ficha (`ID_SGR:` ou a foto) */
  sgrId: string | null;
  plate: string | null;
}): VehicleLinkTarget | null {
  if (input.equipment && input.equipmentIsSgr) return { kind: 'equipment', equipmentId: input.equipment.id };
  if (input.sgrId && /^\d{1,20}$/.test(input.sgrId)) return { kind: 'source_id', sgrId: input.sgrId };
  const plate = (input.plate ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return plate.length >= 3 ? { kind: 'plate', plate } : null;
}

/** rótulos dos links e das páginas, nos termos do tenant */
export function sgrLinkLabels(terms: Pick<Terms, 'resource' | 'contact'>) {
  return { vehicle: `Ver ${terms.resource}`, endCustomer: `Ver ${terms.contact}` };
}

export const capitalize = cap;

// ── a rotina noturna do Recurso (spec 035) ──────────────────────────────────

/**
 * O resumo de uma rodada: "40 de 52 lidos · 83 leituras · 12 ligados · …" — só
 * o que não é zero. Os números são de tickets, menos "lidos" e "não
 * encontrados" (o recurso, nos termos do tenant).
 */
export function routineCountsText(c: SgrRoutineCounts, terms: Pick<Terms, 'resources' | 'client'>): string {
  const parts: string[] = [
    `${c.read} de ${c.vehicles} ${terms.resources} lidos`,
    `${c.reads} ${c.reads === 1 ? 'leitura' : 'leituras'}`,
  ];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(c.linked, 'ligado', 'ligados');
  add(c.filledSlots, 'campo preenchido', 'campos preenchidos');
  add(c.otherClient, `de outro ${terms.client}`, `de outro ${terms.client}`);
  add(c.refreshed, 'já ligado relido', 'já ligados relidos');
  add(c.notFound, 'não encontrado', 'não encontrados');
  add(c.unavailable, 'leitura falhou', 'leituras falharam');
  add(c.skipped, 'mudou no meio', 'mudaram no meio');
  add(c.errors, 'erro ao gravar', 'erros ao gravar');
  return parts.join(' · ');
}
